import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from 'recharts';

import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  colors,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { DashboardAnalyticsItem, Maybe } from '@libs/graphql-types';

interface BreakdownCategoryProps {
  data?: Maybe<DashboardAnalyticsItem>[];
  isLoading?: boolean;
}

const CustomYAxisTick = (props: any) => {
  const { x, y, payload } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-5} y={0} dy={3} textAnchor="end" fill="#666" fontSize={12}>
        {payload.value}
      </text>
    </g>
  );
};

const useStyles = makeStyles({
  root: {
    '& svg': {
      overflow: 'visible !important',
    },
  },
});

const BreakdownCategory: React.FC<BreakdownCategoryProps> = ({
  data = [],
  isLoading = false,
}) => {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const classes = useStyles();

  const displayData = data as DashboardAnalyticsItem[];
  const size = 55;
  const height = displayData.length * size;

  return (
    <Card elevation={0}>
      <CardHeader
        title={
          <Typography variant="h6">{t('v2.breakdownByCategory')}</Typography>
        }
      />
      <CardContent>
        <Box className={classes.root}>
          <ResponsiveContainer width="100%" height={height}>
            <BarChart
              layout="vertical"
              data={displayData}
              margin={{ top: 10, right: 40, bottom: 10, left: 60 }}
            >
              <CartesianGrid
                horizontal={true}
                vertical={false}
                strokeDasharray="3 3"
              />
              <XAxis
                tickLine={false}
                axisLine={false}
                type="number"
                allowDecimals={false}
                domain={[0, 'dataMax']}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                dataKey="key"
                type="category"
                tick={CustomYAxisTick}
                width={140}
              />
              <Bar
                dataKey="value"
                background={{ fill: colors.grey[100] }}
                barSize={12}
                fill={theme.palette.tertiary?.main || '#FFA726'}
                radius={[0, 4, 4, 0]}
              >
                <LabelList dataKey="value" position="right" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BreakdownCategory;
